import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WorkersPage({ onLogout }) {
  const { t } = useLanguage();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", role: "" });

  useEffect(() => {
    loadWorkers();
  }, []);

  const loadWorkers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/workers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkers(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Mitarbeiter");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (editingWorker) {
        await axios.put(`${API}/workers/${editingWorker.id}`, formData, { headers });
        toast.success("Mitarbeiter aktualisiert!");
      } else {
        await axios.post(`${API}/workers`, formData, { headers });
        toast.success("Mitarbeiter hinzugefügt!");
      }
      loadWorkers();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (workerId) => {
    if (!window.confirm(t("confirm_delete_worker"))) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/workers/${workerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("worker_deleted"));
      loadWorkers();
    } catch (error) {
      toast.error("Fehler beim Löschen des Mitarbeiters");
    }
  };

  const handleEdit = (worker) => {
    setEditingWorker(worker);
    setFormData({ name: worker.name, email: worker.email || "", role: worker.role || "" });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingWorker(null);
    setFormData({ name: "", email: "", role: "" });
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6" data-testid="workers-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("workers_title")}
            </h1>
            <p className="text-slate-600 mt-1">{t("manage_team")}</p>
          </div>
          <Button
            onClick={() => setModalOpen(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-worker-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t("add_worker")}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-600">{t("loading")}</div>
        ) : workers.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
            <p className="text-slate-600">{t("no_workers")}</p>
            <Button
              onClick={() => setModalOpen(true)}
              className="mt-4 bg-slate-900 hover:bg-slate-800"
            >
              {t("add_first_worker")}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workers.map(worker => (
              <div
                key={worker.id}
                className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`worker-card-${worker.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">{worker.name}</h3>
                    {worker.role && <p className="text-sm text-slate-600">{worker.role}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(worker)}
                      className="text-slate-600 hover:text-slate-900"
                      data-testid={`edit-worker-${worker.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(worker.id)}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`delete-worker-${worker.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {worker.email && (
                  <p className="text-sm text-slate-500">{worker.email}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={handleCloseModal}>
        <DialogContent data-testid="worker-modal">
          <DialogHeader>
            <DialogTitle>
              {editingWorker ? t("edit_worker") : t("add_worker")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{t("name")} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="worker-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="worker-email-input"
              />
            </div>
            <div>
              <Label htmlFor="role">{t("role")}</Label>
              <Input
                id="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                data-testid="worker-role-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={handleCloseModal} data-testid="cancel-worker-button">
                {t("cancel")}
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-worker-button">
                {editingWorker ? t("update") : t("add")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}